import { StageGuide } from './StageGuide';
import { isTextModel, isVoiceModel } from './modelKinds';
import { submittedAudioSettings, configurationImpact } from './workflowUiState';
import { useBoundAssets } from './assetCatalog';
import { SpeechLines, VoicePicker } from './Speech';
import {
  ArtDirectionEditor,
  ShotPlanView,
  ShotPlanEditor,
  type PlannedShot,
  type ArtDesign,
} from './ShotPlan';
import { MediaGallery, MediaPlayer, VisualPromptEditor, type MediaPreview } from './MediaGallery';
import {
  KeyframeReview,
  KeyframePromptEditor,
  KeyframeGallery,
  KeyframeImage,
  type KeyframePreview,
  ConsistencyReports,
  type KeyframeResult,
} from './KeyframeReview';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  BookOpen,
  Check,
  ChevronRight,
  FileText,
  Pause,
  Play,
  Plus,
  Settings2,
  Sparkles,
  Upload,
} from 'lucide-react';
import { api, type Schema, type Page } from '../api/client';
import { useStudio } from './App';
import { Dialog, Field, Spinner, Badge, useApi } from './shared';
import { type StudioTask, type Asset } from './production';
import { mediaFailureMessage } from './mediaErrors';
import './novel.css';

type Value = string | number | boolean | null | Value[] | { [key: string]: Value };
type Candidate = Record<string, Value>;
type Step = {
  qualityNotes?: string[];
  canCancelRestructure?: boolean;
  id: string;
  kind: string;
  level: number;
  ordinal: number;
  episodeNumber: number;
  state: string;
  hash: string | null;
  taskId?: string;
  result: Candidate;
  sourceIds?: string[];
  task?: StudioTask;
  prompt?: string;
  shotId?: string;
};
type Settings = {
  audioMode?: 'native' | 'tts';
  voiceModelId?: string;
  narratorVoiceId?: string;
  keyframeModelId?: string;
  textModelId: string;
  visualMode: string;
  visualModelId: string;
  instructions: string;
  requestedEpisodes: number;
};
type Flow = {
  characters?: { id: string; displayName: string }[];
  id: string;
  seriesId: string;
  branchId: string;
  title: string;
  stage: string;
  state: string;
  paused: boolean;
  revision: string;
  episodeNumber: number;
  episodeId?: string;
  sourceLength: number;
  sourceCount: number;
  readCount: number;
  error?: string;
  settings: Settings;
  current?: Step;
  steps?: Step[];
  analysis?: Candidate;
  plan?: Candidate;
  design?: Candidate;
  failedTask?: StudioTask;
  keyframes?: KeyframePreview[];
  mediaPreviews?: MediaPreview[];
  production?: Schema<'ProductionDocument'>;
  decisions?: { action: string; created_at: string; detail?: { feedback?: string } }[];
};
type Source = { id: string; title: string; start: number; end: number; body?: string };
const stages = ['analysis', 'plan', 'design', 'script', 'keyframes', 'media', 'render'];
const labels: Record<string, string> = {
  evidence: '本集原文依据',
  script_wait: '本集资料整理',
  analysis: '读懂原文',
  read: '原文阅读',
  merge: '汇总分析',
  plan: '拆集方案',
  design: '角色与画风',
  script: '本集剧本',
  media: '镜头与声音',
  keyframes: '关键帧审批',
  keyframe: '关键帧生成',
  keyframe_check: '人物与构图检查',
  restructure_script: '整理专业分镜',
  cancel_restructure: '取消整理',
  consistency: '人物一致性检查',
  dialogueMode: '说话方式',
  rebuild: '重新制作关键帧',
  regenerate_visual: '重新生成单个画面',
  render: '成片审阅',
  completed: '全剧完成',
  visual: '画面生成',
  voice: '配音',
  summary: '故事梗概',
  characters: '人物',
  name: '姓名',
  description: '描述',
  events: '事件与因果',
  event: '事件',
  worldRules: '世界规则',
  openThreads: '未解伏笔',
  premise: '故事主线',
  adaptationNotes: '改编策略',
  episodes: '分集方案',
  number: '集数',
  title: '标题',
  goal: '本集推进',
  openingHook: '开场钩子',
  endingHook: '结尾悬念',
  sourceIds: '原文依据',
  changes: '改编说明',
  omissions: '省略内容',
  reason: '省略原因',
  visualStyle: '统一画风',
  appearance: '固定外貌与服装',
  speakingStyle: '说话方式',
  voiceProfile: '角色声音',
  language: '语言 / 口音',
  timbre: '声线与音色',
  pace: '语速与节奏',
  delivery: '说话习惯',
  continuityRules: '连续性规则',
  document: '制作稿',
  shots: '分镜',
  dialogue: '对白',
  speaker: '角色',
  durationFrames: '时长（帧，24 帧 / 秒）',
  caption: '字幕',
  handoff: '本集交接',
  characterStates: '人物状态与知情范围',
  nextHook: '下集钩子',
  knowledgeNotes: '人物知情限制',
  musicVolume: '背景音乐音量',
  approve: '批准',
  edit: '人工修改',
  revise: '要求重写',
  pause: '暂停',
  resume: '继续',
  retry: '恢复任务',
  configure: '调整模型',
  sceneDesigns: '共享场景设计',
  propDesigns: '共享道具设计',
  period: '年代与地域',
  layout: '空间布局与家具位置',
  lighting: '光源',
  details: '环境细节',
  material: '材质',
  scale: '尺寸与比例',
  defaultState: '初始状态',
  purpose: '用途与设计理由',
  sync: '同步制作修改',
};
const fieldOrder = [
  'number',
  'title',
  'name',
  'summary',
  'premise',
  'adaptationNotes',
  'visualStyle',
  'description',
  'appearance',
  'speakingStyle',
  'characters',
  'worldRules',
  'events',
  'openThreads',
  'continuityRules',
  'episodes',
  'goal',
  'openingHook',
  'endingHook',
  'changes',
  'omissions',
  'reason',
  'document',
  'script',
  'shots',
  'speaker',
  'dialogue',
  'durationFrames',
  'caption',
  'handoff',
  'characterStates',
  'nextHook',
  'knowledgeNotes',
  'sourceIds',
];
const hidden = new Set([
  'characterIds',
  'id',
  'visualId',
  'voiceId',
  'musicId',
  'sourceInSeconds',
  'previousReleaseId',
]);

function returnedCandidate(step?: Step): Candidate | null {
  if (!step) return null;
  if (step.result && Object.keys(step.result).length) return step.result;
  const raw = step.task?.result as { text?: string } | undefined;
  if (!raw?.text) return null;
  try {
    const value = JSON.parse(
      raw.text
        .trim()
        .replace(/^```(?:json)?\s*/, '')
        .replace(/\s*```$/, ''),
    );
    return value && !Array.isArray(value) && typeof value === 'object' ? value : null;
  } catch {
    return null;
  }
}

export function NovelPage() {
  const { base, workspaceId } = useStudio();
  const { seriesId } = useParams();
  const navigate = useNavigate();
  const [importing, setImporting] = useState(false);
  const list = useQuery({
    queryKey: ['api', base + '/novel-workflows', seriesId],
    queryFn: () =>
      api<Page<Flow>>(`${base}/novel-workflows${seriesId ? '?seriesId=' + seriesId : ''}`),
    refetchInterval: 5000,
  });
  const flowId = seriesId ? list.data?.items[0]?.id : undefined;
  if (flowId && !importing)
    return <Workflow key={flowId} id={flowId} newImport={() => setImporting(true)} />;
  return (
    <div className="page novel-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">NOVEL TO SERIES</span>
          <h1>把一部小说，拍成连续的故事。</h1>
          <p>交给 Agent 逐步改编。重要决定，由你审批。</p>
        </div>
      </div>
      {list.isError && <p className="error-banner">流程读取失败，请刷新重试。</p>}
      {!importing && !seriesId && !!list.data?.items.length && (
        <div className="novel-projects">
          {list.data.items.map((f) => (
            <button
              key={f.id}
              className="novel-project"
              onClick={() => navigate(`/w/${workspaceId}/series/${f.seriesId}/novel`)}
            >
              <BookOpen size={22} />
              <strong>{f.title}</strong>
              <span>
                {labels[f.stage]} ·{' '}
                {f.paused
                  ? '已暂停'
                  : f.state === 'review'
                    ? '等你审批'
                    : f.state === 'failed'
                      ? '需要处理'
                      : f.state === 'completed'
                        ? '已完成'
                        : '进行中'}
              </span>
              <ChevronRight size={18} />
            </button>
          ))}
        </div>
      )}
      <ImportForm
        onDone={(f) => {
          setImporting(false);
          navigate(`/w/${workspaceId}/series/${f.seriesId}/novel`);
        }}
      />
    </div>
  );
}
function ModelFields({ settings, change }: { settings: Settings; change: (s: Settings) => void }) {
  const { base } = useStudio();
  const models = useApi<Page<Schema<'ModelSummary'>>>(`${base}/models`);
  const textModels = models.data?.items.filter((m) => m.selectable && isTextModel(m)) ?? [];
  const visualModels =
    models.data?.items.filter((m) =>
      m.upstreamModelId.toLowerCase().includes(settings.visualMode),
    ) ?? [];
  const keyframeModels =
    models.data?.items.filter((m) => m.upstreamModelId.includes('image')) ?? [];
  const keyframeModel = keyframeModels.find((m) => m.catalogModelId === settings.keyframeModelId);
  const voiceModels = models.data?.items.filter(isVoiceModel) ?? [];
  const audioMode = settings.audioMode ?? (settings.visualMode === 'video' ? 'native' : 'tts');
  return (
    <>
      <Field label="文字模型">
        <select
          value={settings.textModelId}
          onChange={(e) => change({ ...settings, textModelId: e.target.value })}
        >
          <option value="">选择已启用的文字模型</option>
          {textModels.map((m) => (
            <option key={m.catalogModelId} value={m.catalogModelId}>
              {m.displayName}
            </option>
          ))}
        </select>
      </Field>
      <div className="form-row">
        <Field label="画面形式">
          <select
            value={settings.visualMode}
            onChange={(e) =>
              change({
                ...settings,
                visualMode: e.target.value,
                visualModelId: '',
                audioMode: e.target.value === 'image' ? 'tts' : (settings.audioMode ?? 'native'),
              })
            }
          >
            <option value="video">视频短剧</option>
            <option value="image">图片分镜 + 配音</option>
          </select>
        </Field>
        <Field label="画面模型" hint="可以先完成剧本，再选择模型">
          <select
            value={settings.visualModelId}
            onChange={(e) => change({ ...settings, visualModelId: e.target.value })}
          >
            <option value="">稍后选择</option>
            {visualModels.map((m) => (
              <option
                key={m.catalogModelId}
                value={m.catalogModelId}
                disabled={settings.visualMode === 'video' && m.providerKey === 'grok2api'}
              >
                {m.displayName}
                {settings.visualMode === 'video' && m.providerKey === 'grok2api'
                  ? ' · 仅文字生视频，不支持本流程首帧'
                  : m.selectable
                    ? ''
                    : ' · 尚未验证'}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {settings.visualMode === 'video' && (
        <Field
          label="关键帧图片模型"
          hint={
            keyframeModel?.keyframeSupport?.supported
              ? `本系统每镜头最多携带 ${keyframeModel.keyframeSupport.maxReferenceImages} 张角色参考图，超限会提示更换模型或拆分镜头。切换后用于未开始和新建的关键帧候选；已生成的图片保留。`
              : '把角色定妆图带入图片生成，审批静帧后再生成视频。切换模型后，失败镜头需点击生成新候选或修改提示词重试。'
          }
        >
          <select
            value={settings.keyframeModelId ?? ''}
            onChange={(e) => change({ ...settings, keyframeModelId: e.target.value })}
          >
            <option value="">自动选择支持参考图的图片模型</option>
            {keyframeModels.map((m) => (
              <option
                key={m.catalogModelId}
                value={m.catalogModelId}
                disabled={!m.keyframeSupport?.supported}
              >
                {m.displayName} ·{' '}
                {m.keyframeSupport?.supported
                  ? `最多 ${m.keyframeSupport.maxReferenceImages} 张参考图`
                  : m.keyframeSupport?.reason || '尚未接入参考图'}
              </option>
            ))}
          </select>
        </Field>
      )}
      <Field label="声音制作方式">
        <select
          value={audioMode}
          onChange={(e) => change({ ...settings, audioMode: e.target.value as 'native' | 'tts' })}
        >
          {settings.visualMode === 'video' && (
            <option value="native">视频模型直接生成对白与声音</option>
          )}
          <option value="tts">独立角色配音 · 逐句生成后审批</option>
        </select>
      </Field>
      {audioMode === 'tts' ? (
        <>
          <Field label="配音模型">
            <select
              value={settings.voiceModelId ?? ''}
              onChange={(e) => change({ ...settings, voiceModelId: e.target.value })}
            >
              <option value="">选择配音模型</option>
              {voiceModels.map((model) => (
                <option key={model.catalogModelId} value={model.catalogModelId}>
                  {model.displayName}
                  {model.selectable ? '' : ' · 待试听验证'}
                </option>
              ))}
            </select>
          </Field>
          <VoicePicker
            label="旁白音色"
            value={settings.narratorVoiceId ?? 'eve'}
            change={(narratorVoiceId) => change({ ...settings, narratorVoiceId })}
          />
          <p className="quiet">
            角色对白使用角色库中已确认的音色，逐句生成并等你试听审批。独立配音会替换视频原声（含环境音），不会自动调整口型。
          </p>
        </>
      ) : (
        <p className="quiet">
          把批准的对白交给视频模型，生成角色说话的画面与声音；合成时保留视频原声，生成后由你试听审批。
        </p>
      )}
    </>
  );
}
function ImportForm({ onDone }: { onDone: (f: Flow) => void }) {
  const { base, refresh } = useStudio();
  const [title, setTitle] = useState(''),
    [source, setSource] = useState(''),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState<Settings>({
    textModelId: '',
    visualMode: 'video',
    visualModelId: '',
    instructions: '',
    requestedEpisodes: 0,
    narratorVoiceId: 'eve',
  });
  return (
    <div className="novel-import">
      <section className="novel-import-main">
        <div className="section-heading">
          <h2>
            <FileText size={20} /> 导入小说
          </h2>
          <label className="button secondary novel-upload">
            <Upload size={16} />
            读取 TXT
            <input
              type="file"
              accept=".txt,text/plain"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                try {
                  if (file.size > 8_000_000)
                    throw new Error('文件过大，请选择 200 万字符以内的 TXT。');
                  const text = await file.text();
                  if (text.includes('\uFFFD'))
                    throw new Error('文件编码无法识别，请另存为 UTF-8 TXT。');
                  if (text.length > 2_000_000)
                    throw new Error('当前支持最多 200 万字符，请按卷导入。');
                  setSource(text);
                  if (!title) setTitle(file.name.replace(/\.txt$/i, ''));
                  setError('');
                } catch (e) {
                  setError((e as Error).message);
                }
              }}
            />
          </label>
        </div>
        <Field label="小说名称">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={100}
            placeholder="为这部连续短剧起个名字"
          />
        </Field>
        <Field
          label="小说正文"
          hint="支持粘贴或 UTF-8 TXT；最多 200 万字符。原文完整保存，分段读取。"
        >
          <textarea
            className="novel-source-input"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            maxLength={2_000_000}
            placeholder="把小说复制到这里。从人物、伏笔到分集节奏，Agent 会逐步整理给你审阅。"
          />
        </Field>
        <div className="novel-count">
          {source.length.toLocaleString()} / 2,000,000 字符 · 每集 60 秒 · 竖屏
        </div>
        <Field label="改编要求（可选）">
          <textarea
            value={settings.instructions}
            onChange={(e) => setSettings({ ...settings, instructions: e.target.value })}
            maxLength={4000}
            placeholder="例如：忠于原著，保留悬疑感，突出女主成长，减少旁白。"
          />
        </Field>
      </section>
      <aside className="novel-import-side">
        <h3>从原文到成片</h3>
        <ol className="novel-outline">
          {stages.map((s, i) => (
            <li key={s}>
              <span>{i + 1}</span>
              <div>
                <strong>{labels[s]}</strong>
                <small>
                  {
                    [
                      '完整阅读，索引原文依据',
                      '每集一分钟，解释改编取舍',
                      '确定人物与统一美术设定',
                      '先做好第一集，再逐集推进',
                      '生成画面、配音与字幕',
                      '你确认后，才启动下一集',
                    ][i]
                  }
                </small>
              </div>
            </li>
          ))}
        </ol>
        <ModelFields settings={settings} change={setSettings} />
        <Field label="预期集数">
          <input
            aria-label="预期集数"
            type="number"
            min={0}
            max={30}
            value={settings.requestedEpisodes}
            onChange={(e) =>
              setSettings({ ...settings, requestedEpisodes: Number(e.target.value) })
            }
          />
          <small>0 = 由 Agent 建议；本次改编支持 1–30 集。</small>
        </Field>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <button
          className="button primary full"
          disabled={busy || !title.trim() || !source.trim() || !settings.textModelId}
          onClick={async () => {
            setBusy(true);
            setError('');
            try {
              const f = await api<Flow>(`${base}/novel-workflows`, 'POST', {
                title,
                source,
                ...submittedAudioSettings(settings),
                confirmed: true,
              });
              await refresh();
              onDone(f);
            } catch (e) {
              setError((e as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? <Spinner /> : <Sparkles size={16} />}导入并开始阅读
        </button>
        <p className="novel-footnote">
          原文将分段交给所选文字模型。每个创作阶段完成后停下，等你审阅。进度自动保存，可随时回来继续。
        </p>
      </aside>
    </div>
  );
}
function Workflow({ id, newImport }: { id: string; newImport: () => void }) {
  const { base, workspaceId, refresh } = useStudio();
  const navigate = useNavigate(),
    qc = useQueryClient();
  const path = `${base}/novel-workflows/${id}`;
  const query = useQuery({
    queryKey: ['api', path],
    queryFn: () => api<Flow>(path),
    refetchInterval: 2500,
  });
  const f = query.data;
  const sources = useApi<Page<Source>>(path + '/sources');
  const [source, setSource] = useState<Source | null>(null),
    [historical, setHistorical] = useState<Step | null>(null),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [feedback, setFeedback] = useState(''),
    [editing, setEditing] = useState<Candidate | null>(null),
    [editorBase, setEditorBase] = useState<Record<string, unknown>>({}),
    [config, setConfig] = useState<Settings | null>(null),
    [configConfirmed, setConfigConfirmed] = useState(false),
    [reviewed, setReviewed] = useState('');
  const [promptEditor, setPromptEditor] = useState<{
    step: Step;
    revision: string;
    groupId: string;
    groupHash: string;
  } | null>(null);
  const [visualEditor, setVisualEditor] = useState<{
    step: Step;
    number: number;
    revision: string;
    groupId: string;
    groupHash: string;
    productionHash: string;
  } | null>(null);
  useEffect(() => {
    setFeedback('');
    setReviewed('');
  }, [f?.current?.id]);
  useEffect(() => setConfigConfirmed(false), [f?.revision]);
  async function command(action: string, extra: Record<string, unknown> = {}) {
    if (!f) return false;
    setBusy(true);
    setError('');
    try {
      const next = await api<Flow>(path + '/commands', 'POST', {
        action,
        expectedRevision: f.revision,
        ...(f.current ? { stepId: f.current.id, contentHash: f.current.hash ?? '' } : {}),
        ...extra,
      });
      qc.setQueryData(['api', path], next);
      await refresh();
      setEditing(null);
      setConfig(null);
      setFeedback('');
      setReviewed('');
      return true;
    } catch (e) {
      setError((e as Error).message);
      await query.refetch();
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function readSource(ref: string) {
    try {
      setSource(await api<Source>(path + '/sources/' + encodeURIComponent(ref)));
    } catch (e) {
      setError((e as Error).message);
    }
  }
  function editReturned(step: Step) {
    const value = returnedCandidate(step);
    if (!f?.current || !value) return;
    setEditorBase({
      expectedRevision: f.revision,
      stepId: f.current.id,
      contentHash: f.current.hash ?? '',
      sourceStepId: step.id,
    });
    setEditing(structuredClone(value));
    setHistorical(null);
  }
  if (query.isError)
    return (
      <div className="page">
        <p className="error-banner">
          流程读取失败。<button onClick={() => void query.refetch()}>重试</button>
        </p>
      </div>
    );
  if (!f)
    return (
      <div className="page">
        <Spinner />
      </div>
    );
  const current = f.current,
    review = f.state === 'review',
    editable = ['analysis', 'plan', 'design', 'script'].includes(f.stage),
    reviewKey = current?.id + ':' + current?.hash;
  const configImpact = config
    ? configurationImpact(f.stage, f.settings, submittedAudioSettings(config))
    : null;
  const failedFrame =
    f.stage === 'keyframes'
      ? f.steps?.find(
          (s) =>
            s.kind === 'keyframe' && s.state === 'failed' && s.episodeNumber === f.episodeNumber,
        )
      : undefined;
  const failedVisual =
    f.stage === 'media'
      ? f.mediaPreviews?.find((item) => item.kind !== 'voice' && item.state === 'failed')
      : undefined;
  async function inspectMedia(stepId: string) {
    setError('');
    try {
      setHistorical(await api<Step>(path + '/steps/' + stepId));
    } catch (e) {
      setError((e as Error).message);
    }
  }
  async function editVisual(item: MediaPreview) {
    if (!f?.current || !f.production) return;
    setBusy(true);
    setError('');
    try {
      const detail = await api<Step>(path + '/steps/' + item.stepId);
      if (!detail.prompt || !detail.shotId)
        throw new Error('此镜头没有可编辑的提示词，请刷新后重试');
      setVisualEditor({
        step: detail,
        number: item.ordinal + 1,
        revision: f.revision,
        groupId: f.current.id,
        groupHash: f.current.hash ?? '',
        productionHash: f.production.hash,
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function editKeyframe(step: Step) {
    if (!f?.current) return;
    setBusy(true);
    setError('');
    try {
      const detail = await api<Step>(path + '/steps/' + step.id);
      if (!detail.prompt || !detail.shotId)
        throw new Error('此镜头没有可编辑的提示词，请刷新后重试');
      setPromptEditor({
        step: detail,
        revision: f.revision,
        groupId: f.current.id,
        groupHash: f.current.hash ?? '',
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const toProduction = () =>
    navigate(
      `/w/${workspaceId}/series/${f.seriesId}/production?episode=${f.episodeId}&branch=${f.branchId}`,
    );
  return (
    <div className="page novel-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">YOUR ADAPTATION AGENT</span>
          <h1>{f.title}</h1>
          <p>
            {f.sourceLength.toLocaleString()} 字符 · {f.sourceCount} 个原文片段 · 每集 60 秒
            {f.episodeNumber > 0 ? ` · 正在制作第 ${f.episodeNumber} 集` : ''}
          </p>
        </div>
        <div className="novel-toolbar">
          <button
            className="button secondary"
            onClick={() => {
              setConfig(f.settings);
              setConfigConfirmed(false);
            }}
          >
            <Settings2 size={16} />
            模型设置
          </button>
          {f.state !== 'completed' && (
            <button
              className="button secondary"
              disabled={busy}
              onClick={() => void command(f.paused ? 'resume' : 'pause')}
            >
              {f.paused ? <Play size={16} /> : <Pause size={16} />} {f.paused ? '继续' : '暂停'}
            </button>
          )}
          <button className="icon-button" aria-label="导入另一部小说" onClick={newImport}>
            <Plus size={18} />
          </button>
        </div>
      </div>
      <StageGuide
        stage={f.stage}
        state={f.state}
        paused={f.paused}
        visualMode={f.settings.visualMode}
        episodeNumber={f.episodeNumber}
        selectStage={(stage) => {
          if (stage === f.stage) {
            document
              .getElementById('current-workflow-stage')
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
          }
          const step = [...(f.steps ?? [])]
            .reverse()
            .find(
              (item) =>
                (stage === 'analysis'
                  ? ['read', 'merge'].includes(item.kind) && item.state === 'approved'
                  : item.kind === stage) &&
                ['approved', 'ready'].includes(item.state) &&
                (!item.episodeNumber || item.episodeNumber === f.episodeNumber),
            );
          if (step) void inspectMedia(step.id);
          else setError('当前阶段没有可查看的已批准记录，请在右侧“过程与版本”中核对。');
        }}
      />
      {error && (
        <p className="error-banner" role="alert">
          {error}
        </p>
      )}
      {f.paused && (
        <p className="info-box">流程已暂停。已创建的任务会完成并保留结果；恢复后才安排下一步。</p>
      )}
      <div className="novel-workspace">
        <section className="novel-main">
          <div id="current-workflow-stage" className={`novel-status ${review ? 'review' : ''}`}>
            <span className="novel-status-icon">
              {review ? (
                <BookOpen size={24} />
              ) : f.state === 'completed' ? (
                <Check size={24} />
              ) : f.state === 'running' && !f.paused ? (
                <Spinner />
              ) : (
                <Pause size={24} />
              )}
            </span>
            <div>
              <h2>
                {f.state === 'completed'
                  ? '这部连续短剧已完成'
                  : review
                    ? `${labels[f.stage]}，等你审批`
                    : f.state === 'failed'
                      ? '流程停在这里，进度已保存'
                      : f.paused
                        ? '已暂停'
                        : `${labels[f.stage]}进行中`}
              </h2>
              <p>
                {f.state === 'failed'
                  ? f.failedTask?.errorMessage
                    ? mediaFailureMessage(f.failedTask)
                    : f.error
                  : f.stage === 'analysis'
                    ? `已读完 ${f.readCount} / ${f.sourceCount} 段。所有片段读取后，再整理完整分析。`
                    : review
                      ? '审阅下方结果，可以直接修改或写下意见让 Agent 重写。'
                      : f.state === 'completed'
                        ? '所有成片和每一次审批都保留在项目中。'
                        : '页面关闭后仍会继续；需要你做决定时会停下。'}
              </p>
            </div>
          </div>
          {f.state === 'failed' && (
            <div className="novel-recovery">
              {failedFrame && (
                <button
                  className="button primary"
                  disabled={busy}
                  onClick={() => void editKeyframe(failedFrame)}
                >
                  修改第 {failedFrame.ordinal + 1} 镜提示词并重试
                </button>
              )}
              {failedVisual && (
                <button
                  className="button primary"
                  disabled={busy}
                  onClick={() => void editVisual(failedVisual)}
                >
                  修改第 {failedVisual.ordinal + 1} 镜画面提示词并重试
                </button>
              )}
              {['media', 'keyframes'].includes(f.stage) && (
                <button
                  className="button secondary"
                  onClick={() => navigate(`/w/${workspaceId}/series/${f.seriesId}/characters`)}
                >
                  打开角色库确认参考图
                </button>
              )}
              <button
                className="button secondary"
                disabled={busy || (!!f.failedTask && !f.failedTask.canRecover)}
                onClick={() => void command('retry')}
              >
                恢复原任务 / 流程
              </button>
              {editable && current && returnedCandidate(current) && (
                <button
                  className="button secondary"
                  disabled={busy}
                  onClick={() => editReturned(current)}
                >
                  修改已返回的草稿
                </button>
              )}
              {f.failedTask && !failedFrame && !failedVisual && (
                <button
                  className="button secondary"
                  disabled={busy}
                  onClick={() => {
                    if (
                      window.confirm(
                        f.failedTask?.kind === 'qa'
                          ? '只重新检查失败镜头并生成报告，保留已有视频和已完成检查。请确认已核对原请求状态。'
                          : '这会发起一次新的生成请求。请确认已核对原任务状态，再生成新候选。',
                      )
                    )
                      void command('retry', { confirmNewRequest: true });
                  }}
                >
                  {f.failedTask.kind === 'qa' ? '重新检查失败镜头' : '生成新候选'}
                </button>
              )}
              <p>
                {failedFrame
                  ? '已成功的关键帧会保留。可以查看并替换失败镜头的完整提示词，再生成新候选；尚未生成的镜头随后继续。'
                  : failedVisual
                    ? '已生成镜头可在下方播放。修改失败镜头提示词后可单独重试，其他成功结果保留。'
                    : f.failedTask?.kind === 'qa'
                      ? '人物一致性检查未完成，已生成的视频和已完成报告均保留。重新检查只调用看图模型，不重做视频；看图请求最长等待 10 分钟。'
                      : f.failedTask?.errorCode?.startsWith('CONTENT_REVIEW_')
                        ? '这是旧版额外审查留下的失败记录。可恢复已保存的原稿，无需重新调用模型，再由你审阅。'
                        : f.failedTask?.canRecover
                          ? '可读取已保存的原结果，不重新发送模型请求。'
                          : f.failedTask
                            ? '没有可恢复的原结果。核对后可明确发起新请求。'
                            : '处理提示的问题后，可以继续原流程。'}
              </p>
              {editable && current && (
                <div className="novel-feedback">
                  <Field label="修改意见">
                    <textarea
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      maxLength={4000}
                      placeholder="例如：调整本集冲突、人物设定或镜头表达。"
                    />
                  </Field>
                  <button
                    className="button secondary"
                    disabled={busy || !feedback.trim() || f.paused}
                    onClick={() => void command('revise', { feedback })}
                  >
                    按意见重写，仍需我审批
                  </button>
                </div>
              )}
            </div>
          )}
          <p className="novel-footnote">拆集、人设、剧本和分镜先保存为候选，等待你审阅后再继续。</p>
          {!!f.mediaPreviews?.length && (
            <MediaGallery
              items={f.mediaPreviews}
              busy={busy}
              editable={f.stage === 'media' && (review || f.state === 'failed') && !f.paused}
              inspect={(stepId) => void inspectMedia(stepId)}
              edit={(item) => void editVisual(item)}
              fps={
                f.production
                  ? f.production.format.fpsNumerator / f.production.format.fpsDenominator
                  : 24
              }
              regenerateVoice={(item, lineIndex) =>
                void command('regenerate_voice', {
                  sourceStepId: item.stepId,
                  shotId: item.shotId,
                  productionHash: f.production?.hash,
                  confirmNewRequest: true,
                  ...(lineIndex === undefined ? {} : { lineIndex }),
                })
              }
            />
          )}
          {!!f.keyframes?.length && !(f.stage === 'keyframes' && review) && (
            <KeyframeGallery items={f.keyframes} />
          )}
          {current && review && (
            <article className="novel-candidate" key={current.id}>
              <div className="section-heading">
                <h2>
                  {labels[f.stage]}
                  {f.episodeNumber > 0 ? ` · 第 ${f.episodeNumber} 集` : ''}
                </h2>
                <Badge>待审批</Badge>
              </div>
              {!!current.qualityNotes?.length && (
                <div className="info-box">
                  <strong>分镜审阅提醒</strong>
                  {current.qualityNotes.map((note, i) => (
                    <p key={i}>{note}</p>
                  ))}
                </div>
              )}
              {f.stage === 'render' ? (
                <>
                  {(current.result.asset as unknown as Asset)?.url && (
                    <video
                      className="novel-preview"
                      controls
                      preload="metadata"
                      src={(current.result.asset as unknown as Asset).url}
                    />
                  )}
                  <p>完整观看后确认本集。定稿留在工作室，Agent 将据此承接下一集。</p>
                  <CandidateView
                    data={(f.production?.body.handoff ?? {}) as unknown as Candidate}
                    readSource={readSource}
                  />
                </>
              ) : f.stage === 'keyframes' ? (
                <KeyframeReview
                  result={current.result as unknown as KeyframeResult}
                  busy={busy || f.paused}
                  regenerate={(shotId, feedback) =>
                    void command('rebuild', { confirmNewRequest: true, shotId, feedback })
                  }
                  editPrompt={(shotId) => {
                    const step = f.steps?.find(
                      (s) =>
                        s.kind === 'keyframe' &&
                        s.state === 'ready' &&
                        s.episodeNumber === f.episodeNumber &&
                        s.ordinal ===
                          f.production?.body.shots.findIndex((shot) => shot.id === shotId),
                    );
                    if (step) void editKeyframe(step);
                  }}
                />
              ) : f.stage === 'media' ? (
                <>
                  {!f.mediaPreviews?.length && (
                    <MediaReview
                      document={current.result.document as unknown as Schema<'ProductionDraft'>}
                      base={base}
                    />
                  )}
                  <ConsistencyReports
                    reports={
                      (current.result.consistencyReports ?? []) as unknown as Parameters<
                        typeof ConsistencyReports
                      >[0]['reports']
                    }
                    document={current.result.document as unknown as Schema<'ProductionDraft'>}
                  />
                </>
              ) : (
                <CandidateView data={current.result} readSource={readSource} />
              )}
              {['design', 'keyframes', 'media'].includes(f.stage) && (
                <p className="novel-footnote">
                  视频模式会携带出场角色的已确认参考图，每个镜头最多 7 张；请先到角色库选图并确认。
                  {f.settings.audioMode === 'tts' || f.settings.visualMode === 'image'
                    ? '独立配音按角色已确认音色逐句生成；请试听每句，确认后再批准素材。它会替换视频原声，不自动调整口型。'
                    : '对白和声音由视频模型直接生成，镜头自动继承角色声音设定；声音仍需逐镜试听。'}
                  图片模式不生成角色开口视频。
                  <button
                    className="button secondary"
                    onClick={() => navigate(`/w/${workspaceId}/series/${f.seriesId}/characters`)}
                  >
                    打开角色库确认参考图
                  </button>
                </p>
              )}
              <div className="novel-approval">
                <label className="novel-check">
                  <input
                    type="checkbox"
                    checked={reviewed === reviewKey}
                    onChange={(e) => setReviewed(e.target.checked ? reviewKey : '')}
                  />
                  {f.stage === 'render'
                    ? '我已完整观看成片，确认本集事实和交接'
                    : f.stage === 'keyframes'
                      ? '我已逐镜核对检查报告、出场人数、角色身份、站位和道具，确认此版关键帧'
                      : '我已审阅当前版本，同意按此继续'}
                </label>
                <div className="novel-actions">
                  <button
                    className="button primary"
                    disabled={
                      busy ||
                      f.paused ||
                      reviewed !== reviewKey ||
                      (f.stage === 'media' &&
                        !!f.mediaPreviews?.some(
                          (item) => item.kind === 'voice' && item.timingValid === false,
                        ))
                    }
                    onClick={() => void command('approve', { humanReviewed: true })}
                  >
                    <Check size={16} />
                    {f.stage === 'render'
                      ? '定稿本集，继续下一集'
                      : f.stage === 'media'
                        ? '批准素材，合成预览'
                        : f.stage === 'keyframes'
                          ? '批准关键帧，生成视频'
                          : '批准并继续'}
                    <ArrowRight size={16} />
                  </button>
                  {f.stage === 'media' &&
                    f.mediaPreviews?.some(
                      (item) => item.kind === 'voice' && item.timingValid === false,
                    ) && (
                      <p className="form-error">
                        有配音尚未完整通过时间检查，请按提示处理并重新生成后审批。
                      </p>
                    )}
                  {editable && (
                    <button
                      className="button secondary"
                      disabled={busy}
                      onClick={() => {
                        setEditorBase({
                          expectedRevision: f.revision,
                          stepId: current.id,
                          contentHash: current.hash,
                        });
                        setEditing(structuredClone(current.result));
                      }}
                    >
                      直接修改
                    </button>
                  )}
                  {f.episodeId && (
                    <button className="button secondary" onClick={toProduction}>
                      打开制作页
                    </button>
                  )}
                </div>
                {editable && (
                  <div className="novel-feedback">
                    <Field label="修改意见">
                      <textarea
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        maxLength={4000}
                        placeholder="例如：第 2 集悬念太弱，把发现信件留到结尾；其他部分保留。"
                      />
                    </Field>
                    <button
                      className="button secondary"
                      disabled={busy || !feedback.trim() || f.paused}
                      onClick={() => void command('revise', { feedback })}
                    >
                      按意见重写，仍需我审批
                    </button>
                  </div>
                )}
              </div>
            </article>
          )}
          {['keyframes', 'media'].includes(f.stage) &&
            (review || f.state === 'failed') &&
            f.production && (
              <div className="info-box novel-sync">
                <div>
                  <strong>整理为专业分镜</strong>
                  <p>
                    保留剧情、人物和完整台词，按主要动作拆镜，分开安排入场、特写与交谈，补齐说话对象、听者反应和结束状态。调用文字模型生成新候选，审批后才替换制作稿；旧图片和视频保留在历史记录中。
                  </p>
                </div>
                <button
                  className="button secondary"
                  disabled={busy || f.paused}
                  onClick={() =>
                    void command('restructure_script', {
                      confirmNewRequest: true,
                      productionHash: f.production!.hash,
                    })
                  }
                >
                  整理专业分镜，交给我审批
                </button>
              </div>
            )}
          {current?.canCancelRestructure && (review || f.state === 'failed') && (
            <button
              className="button secondary"
              disabled={busy}
              onClick={() => void command('cancel_restructure')}
            >
              取消本次整理，返回原阶段
            </button>
          )}
          {['keyframes', 'media'].includes(f.stage) &&
            (review || f.state === 'failed') &&
            f.settings.visualMode === 'video' && (
              <div className="info-box novel-sync">
                <span>
                  人物或造型不一致时，可以重新制作关键帧；现有视频和历史候选会保留。需要全部重新生成时使用此按钮。
                </span>
                <button
                  className="button secondary"
                  disabled={busy || f.paused}
                  onClick={() => void command('rebuild', { confirmNewRequest: true })}
                >
                  重新制作本集关键帧
                </button>
              </div>
            )}
          {f.state === 'running' && ['media', 'keyframes'].includes(f.stage) && (
            <div className="novel-progress-list">
              {f.steps
                ?.filter(
                  (s) =>
                    s.episodeNumber === f.episodeNumber &&
                    ['keyframe', 'keyframe_check', 'visual', 'voice', 'consistency'].includes(
                      s.kind,
                    ) &&
                    s.state !== 'superseded',
                )
                .map((s, i) => (
                  <div key={s.id}>
                    <span>
                      {labels[s.kind]} {i + 1}
                    </span>
                    <Badge>
                      {s.state === 'ready'
                        ? '已完成'
                        : s.state === 'working'
                          ? '生成中'
                          : '等待生成'}
                    </Badge>
                  </div>
                ))}
            </div>
          )}
          {['media', 'render'].includes(f.stage) &&
            f.production &&
            (review || f.state === 'failed') && (
              <div className="info-box novel-sync">
                <span>可在制作页替换画面、调整配音或字幕，保存后回这里同步并重新审阅。</span>
                <button className="button secondary" onClick={toProduction}>
                  打开制作页
                </button>
                <button
                  className="button secondary"
                  disabled={busy}
                  onClick={() => void command('sync', { productionHash: f.production?.hash })}
                >
                  同步已保存的修改
                </button>
              </div>
            )}
          {f.plan && Object.keys(f.plan).length > 0 && (
            <details className="novel-details">
              <summary>已批准的拆集方案</summary>
              <CandidateView data={f.plan} readSource={readSource} />
            </details>
          )}
          {!!f.production?.releases.length && (
            <div className="novel-releases">
              <h2>已定稿成片</h2>
              {f.production.releases.map((r) => (
                <div key={r.id}>
                  <video controls preload="metadata" src={r.asset.url} />
                  <p>{r.handoff.summary}</p>
                  <a className="button secondary" href={r.asset.url} download>
                    下载成片
                  </a>
                </div>
              ))}
            </div>
          )}
        </section>
        <aside className="novel-side">
          <section>
            <h3>
              <BookOpen size={17} />
              原文索引
            </h3>
            <p>候选稿中的原文编号可点开核对。</p>
            <div className="novel-source-list">
              {sources.data?.items.map((s) => (
                <button key={s.id} onClick={() => void readSource(s.id)}>
                  <span>{s.id}</span>
                  <strong>{s.title || '原文片段'}</strong>
                  <small>{s.end - s.start} 字符</small>
                </button>
              ))}
            </div>
          </section>
          <section>
            <h3>过程与版本</h3>
            <div className="novel-history">
              {f.steps
                ?.filter((s) => ['ready', 'approved', 'superseded', 'failed'].includes(s.state))
                .slice(-40)
                .reverse()
                .map((s) => (
                  <button
                    key={s.id}
                    onClick={async () => {
                      try {
                        setHistorical(await api<Step>(path + '/steps/' + s.id));
                      } catch (e) {
                        setError((e as Error).message);
                      }
                    }}
                  >
                    <span>
                      {labels[s.kind]}
                      {s.episodeNumber
                        ? ` · 第 ${s.episodeNumber} 集`
                        : s.ordinal
                          ? ` · ${s.ordinal}`
                          : ''}
                    </span>
                    <small>
                      {
                        (
                          {
                            approved: '已批准',
                            ready: '已完成',
                            superseded: '历史版本',
                            failed: '未完成',
                          } as Record<string, string>
                        )[s.state]
                      }
                    </small>
                  </button>
                ))}
            </div>
          </section>
          <section>
            <h3>审批记录</h3>
            {f.decisions?.slice(0, 8).map((d, i) => (
              <p key={i}>
                {labels[d.action] ?? d.action} · {new Date(d.created_at).toLocaleString('zh-CN')}
                {d.detail?.feedback && (
                  <span className="novel-decision-feedback">{d.detail.feedback}</span>
                )}
              </p>
            ))}
          </section>
        </aside>
      </div>
      {source && (
        <Dialog
          title={`${source.id} · 原文依据`}
          subtitle={`第 ${source.start + 1}–${source.end} 字符`}
          close={() => setSource(null)}
        >
          <div className="dialog-body novel-source-text">{source.body}</div>
        </Dialog>
      )}
      {promptEditor && (
        <KeyframePromptEditor
          key={promptEditor.step.id}
          number={promptEditor.step.ordinal + 1}
          original={promptEditor.step.prompt!}
          busy={busy}
          paused={f.paused}
          error={
            error || (promptEditor.step.task ? mediaFailureMessage(promptEditor.step.task) : '')
          }
          close={() => {
            if (!busy) setPromptEditor(null);
          }}
          submit={async (keyframePrompt) => {
            if (
              await command('rebuild', {
                expectedRevision: promptEditor.revision,
                stepId: promptEditor.groupId,
                contentHash: promptEditor.groupHash,
                shotId: promptEditor.step.shotId,
                confirmNewRequest: true,
                keyframePrompt,
              })
            )
              setPromptEditor(null);
          }}
        />
      )}
      {visualEditor && (
        <VisualPromptEditor
          key={visualEditor.step.id}
          number={visualEditor.number}
          original={visualEditor.step.prompt!}
          video={f.settings.visualMode === 'video'}
          busy={busy}
          paused={f.paused}
          error={
            error || (visualEditor.step.task ? mediaFailureMessage(visualEditor.step.task) : '')
          }
          close={() => {
            if (!busy) setVisualEditor(null);
          }}
          submit={async (visualPrompt) => {
            if (
              await command('regenerate_visual', {
                expectedRevision: visualEditor.revision,
                stepId: visualEditor.groupId,
                contentHash: visualEditor.groupHash,
                sourceStepId: visualEditor.step.id,
                shotId: visualEditor.step.shotId,
                productionHash: visualEditor.productionHash,
                confirmNewRequest: true,
                visualPrompt,
              })
            )
              setVisualEditor(null);
          }}
        />
      )}
      {historical && (
        <Dialog
          title={`${labels[historical.kind]} · ${historical.state === 'superseded' ? '历史版本' : '生成记录'}`}
          subtitle="查看本次生成的提示词、结果和错误记录"
          close={() => setHistorical(null)}
        >
          <div className="dialog-body">
            {historical.kind === 'voice' && historical.task?.result.speechLines && (
              <SpeechLines
                lines={historical.task.result.speechLines}
                fps={
                  f.production
                    ? f.production.format.fpsNumerator / f.production.format.fpsDenominator
                    : 24
                }
              />
            )}
            {['keyframe', 'visual'].includes(historical.kind) && historical.prompt && (
              <details className="novel-details" open>
                <summary>
                  {historical.kind === 'keyframe' ? '本次关键帧提示词' : '本次画面提示词'}
                </summary>
                <p className="novel-source-text">{historical.prompt}</p>
              </details>
            )}
            {historical.kind === 'keyframes' && historical.result.items ? (
              <KeyframeReview
                result={historical.result as unknown as KeyframeResult}
                busy={false}
              />
            ) : historical.kind === 'keyframe' &&
              (historical.result.asset as unknown as Asset)?.url ? (
              <KeyframeImage
                asset={historical.result.asset as unknown as Asset}
                label={`第 ${historical.ordinal + 1} 镜历史关键帧`}
              />
            ) : ['visual', 'voice'].includes(historical.kind) &&
              ((historical.result.asset ?? historical.task?.result.asset) as unknown as Asset)
                ?.url ? (
              <MediaPlayer
                asset={
                  (historical.result.asset ?? historical.task?.result.asset) as unknown as Asset
                }
                label="镜头历史候选"
              />
            ) : historical.kind === 'media' && historical.result.document ? (
              <MediaReview
                document={historical.result.document as unknown as Schema<'ProductionDraft'>}
                base={base}
              />
            ) : (
              <CandidateView
                data={returnedCandidate(historical) ?? historical.result}
                readSource={readSource}
              />
            )}
            {current &&
              historical.kind === current.kind &&
              historical.episodeNumber === current.episodeNumber &&
              editable &&
              (review || f.state === 'failed') &&
              returnedCandidate(historical) && (
                <button className="button secondary" onClick={() => editReturned(historical)}>
                  用此稿修改为新候选
                </button>
              )}
            {historical.task?.errorMessage && <p>{mediaFailureMessage(historical.task)}</p>}
          </div>
        </Dialog>
      )}
      {editing && (
        <Dialog
          title="修改当前候选"
          subtitle="保存为新候选，原版本保留；保存后仍需审批"
          close={() => setEditing(null)}
        >
          <div className="dialog-body">
            <CandidateEditor
              design={(editing.document ?? editing) as unknown as ArtDesign}
              roles={(f.characters ?? []).map((c) => ({ characterId: c.id, name: c.displayName }))}
              value={editing}
              change={(v) => setEditing(v as Candidate)}
            />
            {error && <p className="form-error">{error}</p>}
            <button
              className="button primary"
              disabled={busy}
              onClick={() => void command('edit', { candidate: editing, ...editorBase })}
            >
              保存新候选
            </button>
          </div>
        </Dialog>
      )}
      {config && (
        <Dialog
          title="流程模型设置"
          subtitle="用于后续新任务；已发出的任务保留原模型"
          close={() => setConfig(null)}
        >
          <div className="dialog-body">
            <ModelFields
              settings={config}
              change={(next) => {
                setConfig(next);
                setConfigConfirmed(false);
              }}
            />
            {configImpact?.blocked ? (
              <p className="form-error">本集已进入成片阶段，请先返回制作并同步，再切换制作方式。</p>
            ) : configImpact?.rebuild ? (
              <section className="info-box">
                <div>
                  <strong>保存此修改会重新调用生成模型</strong>
                  <p>{configImpact.detail}历史素材保留，结果仍需审批。调用可能消耗上游额度。</p>
                  <label className="novel-check">
                    <input
                      type="checkbox"
                      checked={configConfirmed}
                      onChange={(event) => setConfigConfirmed(event.target.checked)}
                    />
                    我确认上述影响，并同意发起新的生成请求
                  </label>
                </div>
              </section>
            ) : configImpact?.changed ? (
              <p className="quiet">新设置用于后续制作，已保存的历史素材保留。</p>
            ) : null}
            {error && <p className="form-error">{error}</p>}
            <button
              className="button primary"
              disabled={
                busy ||
                !config.textModelId ||
                !!configImpact?.blocked ||
                (!!configImpact?.rebuild && !configConfirmed)
              }
              onClick={() =>
                void command('configure', {
                  textModelId: config.textModelId,
                  visualModelId: config.visualModelId,
                  visualMode: config.visualMode,
                  keyframeModelId: config.keyframeModelId ?? '',
                  audioMode: submittedAudioSettings(config).audioMode,
                  voiceModelId: config.voiceModelId ?? '',
                  narratorVoiceId: submittedAudioSettings(config).narratorVoiceId,
                  ...(configImpact?.rebuild ? { confirmNewRequest: configConfirmed } : {}),
                })
              }
            >
              {configImpact?.rebuild ? '确认修改并重建本集素材' : '保存设置'}
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
}
function CandidateView({
  data,
  readSource,
}: {
  data: Candidate;
  readSource: (id: string) => void;
}) {
  const render = (value: Value, key: string, depth: number): ReactNode => {
    if (hidden.has(key) || key === 'voice' || value === null) return null;
    if (key === 'sourceIds' && Array.isArray(value))
      return (
        <div className="novel-citations">
          {value.map((ref) => (
            <button key={String(ref)} onClick={() => readSource(String(ref))}>
              {String(ref)}
            </button>
          ))}
        </div>
      );
    if (Array.isArray(value))
      return (
        <div className="novel-values">
          {!value.length ? (
            <span className="quiet">无</span>
          ) : (
            value.map((v, i) => (
              <div className={v && typeof v === 'object' ? 'novel-item' : 'novel-line'} key={i}>
                {v && typeof v === 'object' ? (
                  <small className="novel-item-number">{i + 1}</small>
                ) : null}
                {render(v, '', depth + 1)}
              </div>
            ))
          )}
        </div>
      );
    if (
      typeof value === 'object' &&
      value &&
      !Array.isArray(value) &&
      'staging' in value &&
      'dialogueLines' in value
    )
      return (
        <ShotPlanView
          shot={value as unknown as PlannedShot}
          design={(data.document ?? data) as unknown as ArtDesign}
        />
      );
    if (typeof value === 'object')
      return (
        <div>
          {Object.entries(value)
            .sort(([a], [b]) => {
              const ai = fieldOrder.indexOf(a),
                bi = fieldOrder.indexOf(b);
              return (ai < 0 ? 999 : ai) - (bi < 0 ? 999 : bi);
            })
            .map(([k, v]) =>
              hidden.has(k) || k === 'voice' ? null : (
                <div className="novel-field" key={k}>
                  <h4>{labels[k] ?? k}</h4>
                  {render(v, k, depth + 1)}
                </div>
              ),
            )}
        </div>
      );
    return (
      <p>
        {key === 'dialogueMode'
          ? value === 'narration'
            ? '画外旁白 / 内心独白'
            : '角色开口对白'
          : String(value)}
        {key === 'durationFrames' ? `（${Number(value) / 24} 秒）` : ''}
      </p>
    );
  };
  return <div className="novel-content">{render(data, '', 0)}</div>;
}
function CandidateEditor({
  design = {},
  roles = [],
  value,
  change,
  name = '',
}: {
  design?: ArtDesign;
  roles?: { characterId: string; name: string }[];
  value: Value;
  change: (v: Value) => void;
  name?: string;
}) {
  if (hidden.has(name)) return null;
  if (
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    'staging' in value &&
    'dialogueLines' in value
  ) {
    const shot = value as unknown as PlannedShot;
    return (
      <div className="novel-editor-group">
        <Field label="镜头目的与具体描述">
          <textarea
            rows={4}
            value={shot.description}
            onChange={(e) => change({ ...value, description: e.target.value })}
          />
        </Field>
        <Field label="镜头时长（秒）">
          <input
            type="number"
            min={3}
            max={15}
            step={1 / 24}
            value={shot.durationFrames / 24}
            onChange={(e) =>
              change({ ...value, durationFrames: Math.round(Number(e.target.value) * 24) })
            }
          />
        </Field>
        <ShotPlanEditor
          shot={shot}
          roles={roles}
          design={design}
          change={(next) => change(next as unknown as Value)}
        />
      </div>
    );
  }
  if (typeof value === 'boolean')
    return (
      <label className="novel-check">
        <input type="checkbox" checked={value} onChange={(e) => change(e.target.checked)} />
        {labels[name] ?? name}
      </label>
    );

  if (Array.isArray(value))
    return (
      <div className="novel-editor-group">
        <strong>{labels[name] ?? name}</strong>
        {value.map((v, i) => (
          <div className="novel-editor-item" key={i}>
            <CandidateEditor
              roles={roles}
              design={design}
              value={v}
              change={(next) => change(value.map((x, j) => (i === j ? next : x)))}
            />
          </div>
        ))}
      </div>
    );
  if (value && typeof value === 'object')
    return (
      <div>
        {'shots' in value && 'sceneDesigns' in value && (
          <ArtDirectionEditor
            design={value as unknown as ArtDesign}
            change={(next) => change({ ...value, ...next } as unknown as Value)}
          />
        )}
        {Object.entries(value)
          .filter(([k]) => !(['sceneDesigns', 'propDesigns'].includes(k) && 'shots' in value))
          .filter(
            ([k]) =>
              !(
                k === 'script' &&
                Array.isArray(value.shots) &&
                value.shots.some(
                  (shot) =>
                    shot && typeof shot === 'object' && !Array.isArray(shot) && 'staging' in shot,
                )
              ),
          )
          .map(([k, v]) => (
            <CandidateEditor
              roles={roles}
              design={design}
              key={k}
              name={k}
              value={v}
              change={(next) => change({ ...value, [k]: next })}
            />
          ))}
      </div>
    );
  if (typeof value === 'number')
    return (
      <Field label={labels[name] ?? name}>
        <input type="number" value={value} onChange={(e) => change(Number(e.target.value))} />
      </Field>
    );
  return (
    <Field label={labels[name] ?? name}>
      <textarea value={String(value ?? '')} onChange={(e) => change(e.target.value)} />
    </Field>
  );
}
function MediaReview({ document, base }: { document: Schema<'ProductionDraft'>; base: string }) {
  if (!document) return null;
  return (
    <>
      <p>{document.visualStyle}</p>
      <div className="novel-media-grid">
        {document.shots.map((s, i) => (
          <div key={s.id}>
            <h3>
              镜头 {i + 1} · {s.durationFrames / 24} 秒
            </h3>
            <AssetPreview id={s.visualId} base={base} />
            <p>{s.description}</p>
            <p>
              <strong>{s.speaker}</strong> {s.dialogue}
            </p>
            {s.voiceId && (
              <audio
                controls
                preload="metadata"
                src={'/api/v1' + base + '/assets/' + s.voiceId + '/content'}
              />
            )}
          </div>
        ))}
      </div>
    </>
  );
}
function AssetPreview({ id, base }: { id: string; base: string }) {
  const { project } = useStudio();
  const assets = useBoundAssets(project?.id, [id]);
  const asset = assets.data?.items.find((a) => a.id === id);
  const src = '/api/v1' + base + '/assets/' + id + '/content';
  if (!id) return <p className="quiet">本镜尚未绑定画面素材</p>;
  if (assets.isPending) return <p className="quiet">正在读取已绑定的画面…</p>;
  if (!asset)
    return (
      <p className="form-error">
        {assets.error?.message ?? '已绑定素材暂时不可用，请到制作页核对。'}
      </p>
    );
  return asset.mimeType.startsWith('image/') ? (
    <img src={src} alt="待审阅镜头画面" />
  ) : (
    <video controls preload="metadata" src={src} />
  );
}
